import React, { FunctionComponent, useEffect, useMemo, useState } from "react"
import { useDropzone } from "react-dropzone"
import { Paper, Typography, Button, FormHelperText, Grid } from "@material-ui/core"
import { CsvFile } from "./CsvFile"
import styles from "./FileUpload.module.scss"
import { useBatchPageContext } from "./useBatchPageContext"
import { setOriginalFilename, updateFirstTranslate } from "./reducer"

interface FileUploadProps {
    onUpload: (file: CsvFile) => void
    fileValidationErrors?: Error[]
}

export const FileUpload: FunctionComponent<FileUploadProps> = ({
    onUpload,
    fileValidationErrors = []
}) => {
    const { state, dispatch } = useBatchPageContext()
    const [fileTypeError, setFileTypeError] = useState(false)
    const { getRootProps, getInputProps, acceptedFiles, fileRejections } = useDropzone({
        accept: ".csv"
    })

    function acceptFile(file: File) {
        dispatch(setOriginalFilename(file.name))
        setFileTypeError(false)
        dispatch(updateFirstTranslate(true))
        onUpload(new CsvFile(file))
    }

    function rejectFile() {
        dispatch(setOriginalFilename(""))
        setFileTypeError(true)
    }

    useEffect(() => {
        if (acceptedFiles[0]) {
            acceptFile(acceptedFiles[0])
        } else if (fileRejections[0]) {
            rejectFile()
        }
    }, [acceptedFiles, fileRejections])

    const errors = useMemo(() => {
        const messages = []
        if (fileTypeError) {
            messages.push("File type must be .csv")
        }
        if (fileValidationErrors.length > 0) {
            messages.push(...fileValidationErrors.map(error => error.message))
        }
        return messages
    }, [fileTypeError, fileValidationErrors])

    // html input type:file doesn't recognize file as new if same filename as last upload attempt
    // clear event value on click so that new event is recognized
    const clearPreviousFile = (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
        const element = event.target as HTMLInputElement
        element.value = ""
    }

    return (
        <Grid container direction={"column"} spacing={1}>
            <Grid item>
                <input
                    id="file-upload-button"
                    type="file"
                    className={styles.FileUpload__buttonInput}
                    {...getInputProps()}
                    onClick={clearPreviousFile}
                />
                <label htmlFor="file-upload-button">
                    <Button variant="contained" component="span">
                        Choose File
                    </Button>
                </label>
            </Grid>
            <Grid item>
                <Paper
                    id="file-upload-dropzone"
                    data-testid="file-upload-dropzone"
                    className={styles.FileUpload__dropzone}
                    {...getRootProps()}
                >
                    {state.originalFilename && (
                        <Typography variant="h6">{state.originalFilename}</Typography>
                    )}
                    <Typography variant="subtitle1">
                        Drag a .csv file or browse for a .csv file to upload
                        <br /> Must be <b>UTF-8 encoded</b>
                    </Typography>
                </Paper>
            </Grid>
            <Grid item>
                {errors.length > 0 &&
                    errors.map(error => (
                        <FormHelperText className={"file-upload-errors"} error key={error}>
                            {error}
                        </FormHelperText>
                    ))}
            </Grid>
        </Grid>
    )
}
